import type { TFunction } from 'i18next';
import type { WithdrawalStatus } from '../models';

export type WithdrawalStatusBadgeVariant =
  | 'default'
  | 'destructive'
  | 'secondary'
  | 'outline'
  | 'transparent-green'
  | 'transparent-orange'
  | 'transparent-blue'
  | 'transparent-error'
  | 'transparent-bright-error'
  | 'badge-status-4';

/** Estados conocidos devueltos por GET /withdrawals */
export const WITHDRAWAL_STATUSES: WithdrawalStatus[] = [
  'PENDING_REVIEW',
  'IN_PROGRESS',
  'PAID',
  'REJECTED',
  'CANCELLED',
  'RETURNED',
  'FAILED',
];

const STATUS_BADGE_CONFIG: Record<
  WithdrawalStatus,
  { variant: WithdrawalStatusBadgeVariant; labelKey: `status.${string}`; className?: string }
> = {
  PENDING_REVIEW: { variant: 'transparent-orange', labelKey: 'status.pending_review' },
  IN_PROGRESS: { variant: 'transparent-blue', labelKey: 'status.in_progress' },
  PAID: { variant: 'transparent-green', labelKey: 'status.paid' },
  // Token tailwind: bright-error → bg-bright-error (fondo sólido) + texto blanco.
  REJECTED: { variant: 'outline', labelKey: 'status.rejected', className: 'bg-bright-error text-white' },
  // "Cancelado" se diferencia de error con paleta informativa.
  CANCELLED: { variant: 'outline', labelKey: 'status.cancelled', className: 'bg-bg-info text-info' },
  RETURNED: { variant: 'outline', labelKey: 'status.returned' },
  FAILED: { variant: 'transparent-error', labelKey: 'status.failed' },
};

export const isWithdrawalPendingReview = (status: string): boolean => status === 'PENDING_REVIEW';

export const isWithdrawalMarkableAsPaid = (status: string): boolean =>
  status === 'PENDING_REVIEW' || status === 'IN_PROGRESS';

export const getWithdrawalStatusBadgeConfig = (
  status: string,
  t: TFunction<'withdrawalsModule'>,
): { variant: WithdrawalStatusBadgeVariant; label: string; className?: string } => {
  const config = STATUS_BADGE_CONFIG[status as WithdrawalStatus];
  if (config) {
    return { variant: config.variant, label: t(config.labelKey), className: config.className };
  }
  return { variant: 'outline', label: status };
};
